import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { supabase } from './supabase';

let pendingResolve = null;
let pendingReject = null;
let pendingTimeout = null;
let listenerReady = false;

function ensureListener() {
  if (listenerReady) return;
  listenerReady = true;

  App.addListener('appUrlOpen', async ({ url }) => {
    console.log('[OAuth] appUrlOpen fired:', url);

    if (!url.includes('login-callback')) {
      console.log('[OAuth] ignoring URL (no login-callback)');
      return;
    }

    try { await Browser.close(); } catch (_) {}

    if (pendingTimeout) clearTimeout(pendingTimeout);
    const resolve = pendingResolve;
    const reject = pendingReject;
    pendingResolve = null;
    pendingReject = null;
    pendingTimeout = null;

    if (!resolve) {
      // No pending OAuth — this is likely an email confirmation deep link
      // Exchange the code and let the auth state listener handle the rest
      try {
        const hashStr = url.split('#')[1] || '';
        const queryStr = (url.split('?')[1] || '').split('#')[0];
        const hash = new URLSearchParams(hashStr);
        const query = new URLSearchParams(queryStr);
        const code = query.get('code') || hash.get('code');
        if (code) {
          console.log('[OAuth] email confirmation deep link — exchanging code');
          await supabase.auth.exchangeCodeForSession(code);
        }
      } catch (err) {
        console.error('[OAuth] error exchanging email confirmation code:', err);
      }
      return;
    }

    try {
      const hashStr = url.split('#')[1] || '';
      const queryStr = (url.split('?')[1] || '').split('#')[0];
      const hash = new URLSearchParams(hashStr);
      const query = new URLSearchParams(queryStr);

      const access_token = hash.get('access_token') || query.get('access_token');
      const refresh_token = hash.get('refresh_token') || query.get('refresh_token');
      const code = hash.get('code') || query.get('code');

      if (access_token && refresh_token) {
        console.log('[OAuth] setting session with tokens');
        const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
        error ? reject(error) : resolve({ data, error: null });
      } else if (code) {
        console.log('[OAuth] exchanging code for session');
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        error ? reject(error) : resolve({ data, error: null });
      } else {
        const providerError = hash.get('error_description') || query.get('error_description')
          || hash.get('error') || query.get('error');
        console.error('[OAuth] no tokens or code in callback URL:', url);
        reject(new Error(providerError
          ? decodeURIComponent(providerError.replace(/\+/g, ' '))
          : 'No authentication tokens received'));
      }
    } catch (err) {
      console.error('[OAuth] error processing callback:', err);
      reject(err);
    }
  });

  // User tapped "Done" (or the sheet was dismissed) before the callback arrived
  Browser.addListener('browserFinished', () => {
    if (!pendingReject) return;
    console.log('[OAuth] browser closed before callback — cancelling');
    if (pendingTimeout) clearTimeout(pendingTimeout);
    const reject = pendingReject;
    pendingResolve = null;
    pendingReject = null;
    pendingTimeout = null;
    reject(createCancelledError());
  });

  console.log('[OAuth] appUrlOpen listener registered');
}

export const OAUTH_CANCELLED = 'OAUTH_CANCELLED';

function createCancelledError() {
  const err = new Error('Sign-in was cancelled.');
  err.code = OAUTH_CANCELLED;
  return err;
}

// Note: ensureListener() is called lazily inside signInWithOAuthNative()
// rather than at module load, to avoid consuming Universal Link events
// before the deep link handler in universalLinks.js can process them.

/**
 * Detect if the Browser plugin has a real native implementation or is using the
 * web fallback (window.open). The web fallback's open() creates a window.open
 * call which opens Safari — not SFSafariViewController.
 */
function isBrowserPluginNative() {
  try {
    // Capacitor stores registered native plugins; if Browser has a native
    // bridge proxy its constructor name won't be 'BrowserWeb'
    const proto = Object.getPrototypeOf(Browser);
    const ctorName = proto?.constructor?.name;
    console.log('[OAuth] Browser implementation constructor:', ctorName);
    // The web fallback is BrowserWeb; native proxy is typically CapacitorProxy or Object
    if (ctorName === 'BrowserWeb') {
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[OAuth] Could not determine Browser plugin type:', e);
    return true; // assume native if we can't tell
  }
}

export async function signInWithOAuthNative(provider) {
  const native = Capacitor.isNativePlatform();
  console.log('[OAuth] signInWithOAuthNative called, provider:', provider, 'native:', native);

  if (!native) {
    console.log('[OAuth] web mode — using standard redirect');
    return supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { prompt: 'select_account' }
      }
    });
  }

  ensureListener();

  // A previous attempt that never completed shouldn't block a new one
  if (pendingReject) {
    if (pendingTimeout) clearTimeout(pendingTimeout);
    const staleReject = pendingReject;
    pendingResolve = null;
    pendingReject = null;
    pendingTimeout = null;
    staleReject(createCancelledError());
  }

  // Step 1: Get the OAuth URL from Supabase WITHOUT redirecting
  console.log('[OAuth] requesting OAuth URL with skipBrowserRedirect: true');
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      skipBrowserRedirect: true,
      redirectTo: 'app.gettilted.tilted://login-callback',
      // select_account is a Google-only parameter
      ...(provider === 'google' ? { queryParams: { prompt: 'select_account' } } : {})
    }
  });

  if (error) {
    console.error('[OAuth] supabase returned error:', error);
    throw error;
  }

  if (!data?.url) {
    console.error('[OAuth] no URL in response:', data);
    throw new Error('No OAuth URL returned');
  }

  console.log('[OAuth] got OAuth URL:', data.url.substring(0, 120) + '...');
  console.log('[OAuth] full URL length:', data.url.length);

  const hasNativeBrowser = isBrowserPluginNative();
  console.log('[OAuth] Browser plugin is native (not web fallback):', hasNativeBrowser);

  if (!hasNativeBrowser) {
    console.error('[OAuth] WARNING: Browser plugin is using web fallback (window.open = Safari).');
    console.error('[OAuth] This means the native CapacitorBrowser pod is not linked.');
    console.error('[OAuth] On MacinCloud run: cd ios/App && pod install && cd ../..');
  }

  // Step 2: Open URL in SFSafariViewController, wait for deep link callback
  return new Promise((resolve, reject) => {
    pendingResolve = resolve;
    pendingReject = reject;

    pendingTimeout = setTimeout(() => {
      console.warn('[OAuth] timed out after 2 minutes');
      pendingResolve = null;
      pendingReject = null;
      Browser.close().catch(() => {});
      reject(new Error('OAuth sign-in timed out. Please try again.'));
    }, 120_000);

    console.log('[OAuth] calling Browser.open() now...');

    // Open the URL with @capacitor/browser (should use SFSafariViewController)
    Browser.open({
      url: data.url,
      presentationStyle: 'fullscreen',
      toolbarColor: '#0a0a0b'
    }).then(() => {
      console.log('[OAuth] Browser.open() resolved successfully');
    }).catch((err) => {
      console.error('[OAuth] Browser.open() FAILED:', err);
      if (pendingTimeout) clearTimeout(pendingTimeout);
      pendingResolve = null;
      pendingReject = null;
      reject(new Error('Could not open sign-in browser. Please try again.'));
    });
  });
}
